import { API_ENDPOINTS } from '@/constants';
import {
  del,
  get,
  handleResponse,
  patch,
  post,
  put
} from '@/services/base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiResponse } from './types';

// Types for Mountain API
export interface MountainItem {
  id: number;
  name: string;
  created_at: string;
  isFavorite: string;
  status: number;
  default: number;
  config_title?: string;
  config_value?: string;
}

export interface MountainResponse {
  items: MountainItem[];
  page: number;
  total: number;
  limit: number;
}

export interface AddMountainPayload {
  name: string;
  status: number;
  default: number;
  [key: string]: unknown;
}

export interface UpdateMountainPayload {
  id: number;
  name: string;
  status: number;
  default: number;
  [key: string]: unknown;
}

export const useGetMountainListQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.MOUNTAINS_LIST}?${payload}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: { items: [], total: 0 } };
      }
    },
    enabled: !!payload,
    refetchOnWindowFocus: true,
    queryKey: ['mountain-list', payload]
  });

export const useAddMountainMutation = () =>
  useMutation<ApiResponse, Error, AddMountainPayload>({
    mutationFn: (payload: AddMountainPayload) => {
      return post(
        `${API_ENDPOINTS.MOUNTAINS_LIST}`,
        payload as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useMountainDeleteMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (userId: number) => {
      return del(`${API_ENDPOINTS.MOUNTAINS_LIST}/${userId}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUpdateMountainMutation = () =>
  useMutation<ApiResponse, Error, UpdateMountainPayload>({
    mutationFn: (payload: UpdateMountainPayload) => {
      const { id, ...updateData } = payload;
      return put(
        `${API_ENDPOINTS.MOUNTAINS_LIST}/${id}`,
        updateData as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetMountainDetailsQuery = (id: number | null) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(`${API_ENDPOINTS.MOUNTAINS_LIST}/${id}`);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: null };
      }
    },
    enabled: !!id,
    refetchOnWindowFocus: true,
    queryKey: ['mountain-details', id]
  });
export const useUpdateMountainStatusMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (id: number) => {
      return patch(`${API_ENDPOINTS.MOUNTAINS_LIST}/${id}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });
